import { Text, Box, theme, Heading, useMediaQuery } from '@everlywell/ui-kit';
import DynamicIcon, { IconKey } from 'components/DynamicIcon';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import ProductPricingButton from 'components/ProductPricingButton';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { replaceRedirectionLinkRoot } from 'utils/helpers/replaceRedirectionLinkRoot';
import useMdpModals from 'utils/hooks/useMdpModals';
import { CallToAction, LongText } from 'utils/types/contentfulTypes';

// Make this its own separate component folder
import BulletList from './BulletList';

export type HeroV2Props = {
  sectionHeaderIconName?: IconKey;
  sectionHeaderText?: string;
  headline?: string;
  bulletPoints?: LongText;
  backgroundColor?: string;
  desktopBackgroundImage?: IGatsbyImageData;
  desktopBackgroundImageAltText?: string;
  mobileBackgroundImage?: IGatsbyImageData;
  mobileBackgroundImageAltText?: string;
  cta?: CallToAction;
  ctaContext?: {
    ctaParts: {
      priceText: string;
      startingText: string;
      billFrequencyText: string;
      hideSeparator: boolean;
      icon: string;
    };
  };
  disclaimer?: string;
};

/**
 * HeroV2 is a component that displays a hero section with a background image
 * There are 3 different heights for mobile, tablet, and desktop
 * The background image is different for mobile and desktop
 */
const HeroV2 = ({
  sectionHeaderIconName,
  sectionHeaderText,
  backgroundColor = theme.colors.greenCS[700],
  headline,
  bulletPoints,
  mobileBackgroundImage,
  mobileBackgroundImageAltText,
  desktopBackgroundImage,
  desktopBackgroundImageAltText,
  cta,
  disclaimer,
}: HeroV2Props): JSX.Element => {
  const {
    priceText,
    billFrequencyText,
    icon: ctaIcon,
    startingText,
    hideSeparator,
  } = cta?.metadata?.context?.ctaParts || {};
  const { handleMdpCtaClick } = useMdpModals();

  const ctaUrl = cta?.url?.includes('http')
    ? replaceRedirectionLinkRoot(cta.url)
    : `${cta?.url}`;

  const handleCtaClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    handleMdpCtaClick({
      event: event,
      ctaUrl,
    });

    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: headline || sectionHeaderText,
        contentfulSection: 'HeroV2',
        ctaText: cta?.label,
      },
    });
  };

  // Height for smaller devices, like iPhone xs
  const [isHeight667OrLess] = useMediaQuery(`(max-height: 667px)`);

  return (
    <SectionContainer
      size={['fluid', 'fluid', 'fluid', 'fluid', 'ultra']}
      clipPath={[
        'ellipse(250% 60% at 50% 40%)',
        'ellipse(120% 60% at 50% 40%)',
      ]}
      marginY="0"
      marginBottom={[10, null, 20]}
      centerContent
    >
      <Box
        as="section"
        width="100%"
        backgroundRepeat="no-repeat"
        overflow="hidden"
        paddingBottom={[10, 20, 0]}
        backgroundColor={backgroundColor}
      >
        <Box
          display="flex"
          flexDirection={['column', 'column', 'row']}
          gap={['unset', 'unset', 10, 20]}
          maxHeight={['unset', 'unset', '600px']}
          alignItems="center"
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="start"
            flex="1"
            paddingBottom={['unset', 'unset', 6]}
          >
            <Box
              position="relative"
              marginTop={isHeight667OrLess ? '-60px' : '-30px'}
              width="100%"
            >
              {/* Logo and text */}
              <Box
                display="flex"
                flex="1"
                paddingTop="4"
                paddingLeft="4"
                position="absolute"
                paddingBottom={15}
              >
                {sectionHeaderIconName ? (
                  <>
                    <DynamicIcon
                      icon={(sectionHeaderIconName as IconKey) || 'House'}
                      color="white"
                      size="sm"
                      marginRight={1}
                      weight="fill"
                    />
                    <Text marginRight={1} color="white">
                      &#xB7;
                    </Text>{' '}
                  </>
                ) : null}
                <Text
                  color="white"
                  role="header-text"
                  fontSize={['sm', 'sm', 'md']}
                >
                  {sectionHeaderText}
                </Text>
              </Box>
              {/* Mobile and tablet image */}
              <Box
                id="mobile-hero-box"
                flex="3"
                clipPath="circle(57% at 62% 28%)"
                display={['flex', null, 'none']}
                justifyContent="flex-end"
              >
                {mobileBackgroundImage ? (
                  <GatsbyImage
                    image={mobileBackgroundImage}
                    alt={mobileBackgroundImageAltText || ''}
                    imgStyle={{
                      objectFit: 'contain',
                      clipPath: 'circle(57% at 62% 28%)',
                    }}
                  />
                ) : null}
              </Box>
            </Box>

            {/* Text and button container */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              paddingTop={['unset', 'unset', 16, 20]}
              paddingRight={[4, 4, 0, 0]}
              paddingBottom={[12, 12, 16, 20]}
              paddingLeft={[4, 4, 12, 20]}
              width="100%"
            >
              <Box>
                <Heading
                  as="h1"
                  // @ts-ignore
                  size={['32px', '2xl']}
                  color="white"
                  marginBottom={isHeight667OrLess ? 2 : 4}
                >
                  {headline}
                </Heading>
                {bulletPoints ? (
                  <Box lineHeight={2}>
                    <BulletList
                      htmlData={bulletPoints.childMarkdownRemark.html}
                      reduceMargin={isHeight667OrLess}
                    />
                  </Box>
                ) : null}
              </Box>

              {cta && cta.label ? (
                <Box marginTop={8} width={['100%', null, 'fit-content']}>
                  <ProductPricingButton
                    startingText={startingText || ''}
                    priceText={priceText || ''}
                    size="md"
                    billFrequencyText={billFrequencyText || ''}
                    hideSeparator={hideSeparator}
                    buttonText={cta.label}
                    colorScheme="greenCS"
                    buttonIconRight={
                      <DynamicIcon icon={(ctaIcon as IconKey) || 'ArrowDown'} />
                    }
                    onButtonClick={handleCtaClick}
                    linkTo={ctaUrl}
                    id="hero-join-cta"
                  />
                </Box>
              ) : null}

              {disclaimer && (
                <Text color="white" fontSize="sm" opacity={0.6} marginTop={8}>
                  {disclaimer}
                </Text>
              )}
            </Box>
          </Box>

          {/* Desktop image */}
          <Box
            display={['none', 'none', 'flex']}
            flex="1"
            justifyContent="flex-end"
            alignSelf="stretch"
            alignItems="center"
            overflow="hidden"
          >
            {desktopBackgroundImage ? (
              <GatsbyImage
                image={desktopBackgroundImage}
                alt={desktopBackgroundImageAltText || ''}
                imgStyle={{ height: '100%' }}
                style={{
                  height: '100%',
                  clipPath: 'circle(80% at 80% 50%)',
                  minHeight: '600px',
                }}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default HeroV2;
